import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Divider,
  Snackbar,
  CircularProgress,
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useNavigate } from 'react-router-dom';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon
} from '@mui/icons-material';

import LoginService from '../../Services/login/LoginService';
import {
  setloginPointIdSession,
  setloginSession,
  setUserName,
  setUserPlaceSession,
  setUserTypeSession
} from '../Utils/Common';

import ngpicon from '../../component/img/ngpillai.png';
import bgImage from '../../component/img/login1.jpg';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* ---------- UI ---------- */
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const handleClose = () => setOpen(false);

  /* ---------- FLOW ---------- */
  const [step, setStep] = useState('LOGIN');
  const [pageLoading, setPageLoading] = useState(true);

  /* ---------- LOGIN ---------- */
  const [values, setValues] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* ---------- OTP ---------- */
  const [otp, setOtp] = useState('');
  const [serverOtp, setServerOtp] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [loginUser, setLoginUser] = useState(null);

  /* ---------- FORGOT PASSWORD ---------- */
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotUserId, setForgotUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const [isForgotFlow, setIsForgotFlow] = useState(false);


  /* ---------- PAGE LOADER ---------- */
  useEffect(() => {
    setTimeout(() => setPageLoading(false), 1000);
  }, []);

  /* ---------- LOGIN API ---------- */
  const loginApi = () => {
    if (!values.username || !values.password) {
      setSeverity('warning');
      setMessage('Username and Password are required');
      setOpen(true);
      return;
    }

    setLoginLoading(true);

    LoginService.Login({
      userName: values.username,
      password: values.password
    })
      .then(res => {
        const data = res.data[0];

        if (data.message === 'SUCCESS') {
          setLoginUser(data);

          const pno = btoa('9874563215');
          const mail = btoa(data.email_id);

          LoginService.request_send_log(pno, mail)
            .then(resp => {
              const encoded = resp.data.data;
              const extracted = encoded.substring(17);
              const otpVal = Number(extracted) - 7410258;

              setServerOtp(String(otpVal));
              setIsForgotFlow(false);

              setStep('OTP');

              setSeverity('success');
              setMessage('OTP sent to your registered mail id');
              setOpen(true);
            })
            .finally(() => {
              setLoginLoading(false);
            });
        } else {
          setLoginLoading(false);
          setSeverity('error');
          setMessage(data.message);
          setOpen(true);
        }
      })
      .catch(err => {
        setLoginLoading(false);
        setSeverity('error');
        setMessage(err.message || 'Login failed. Please try again.');
        setOpen(true);
      });
  };

  /* ---------- OTP VERIFY ---------- */
  const verifyOtp = () => {
    if (!otp || otp.length !== 4) {
      setSeverity('warning');
      setMessage('Please enter a valid 4-digit OTP');
      setOpen(true);
      return;
    }

    setOtpVerifying(true);

    setTimeout(() => {
      if (Number(otp) === Number(serverOtp)) {
        if (isForgotFlow) {  // ✅ use the flag instead
          setOtpVerifying(false);
          setStep('RESET_PASSWORD');
          return;
        }


        // Normal login flow
        setloginSession(loginUser.userId, loginUser.currentDate);
        setloginPointIdSession(loginUser.pointId);
        setUserPlaceSession(loginUser.placeName);
        setUserTypeSession(loginUser.userType);
        setUserName(loginUser.userName);

        setSeverity('success');
        setMessage('Login successful!');
        setOpen(true);

        setTimeout(() => {
          navigate('/home');
        }, 500);
      } else {
        setOtpVerifying(false);
        setSeverity('error');
        setMessage('Invalid OTP. Please try again.');
        setOpen(true);
      }
    }, 500);
  };

  /* ---------- HANDLE KEY PRESS ---------- */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (step === 'LOGIN') {
        loginApi();
      } else if (step === 'OTP') {
        verifyOtp();
      } else if (step === 'FORGOT_EMAIL') {
        submitForgotEmail();
      } else if (step === 'RESET_PASSWORD') {
        submitResetPassword();
      }
    }
  };

  /* ---------- FORGOT PASSWORD FLOW ---------- */
  const handleForgotPassword = () => {
    setIsForgotFlow(true);
    setStep('FORGOT_EMAIL');
  };

  const handleBackToLogin = () => setStep('LOGIN');

  const submitForgotEmail = () => {
    if (!forgotEmail) {
      setSeverity('warning');
      setMessage('Enter registered email');
      setOpen(true);
      return;
    }

    setLoginLoading(true);

    LoginService.updateEmpPassword({
      user_fiels: forgotEmail,
      pws: null,
      user_id: null,
      flag: '1'
    })
      .then(res => {
        setForgotUserId(res.data[0].user_id);

        const pno = btoa('9874563215');
        const mail = btoa(res.data[0].email_id);

        return LoginService.request_send_log(pno, mail);
      })
      .then(resp => {
        const otpVal = Number(resp.data.data.substring(17)) - 7410258;
        setServerOtp(String(otpVal));
        setStep('OTP');

        setSeverity('success');
        setMessage('OTP sent to your email');
        setOpen(true);
      })
      .finally(() => setLoginLoading(false));
  };

  const submitResetPassword = () => {
    if (!newPassword || !confirmPassword) {
      setSeverity('warning');
      setMessage('All fields required');
      setOpen(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setSeverity('error');
      setMessage('Passwords do not match');
      setOpen(true);
      return;
    }

    setResetLoading(true);

    LoginService.updateEmpPassword({
      user_fiels: btoa(forgotEmail),
      pws: newPassword,
      user_id: forgotUserId,
      flag: '2'
    })
      .then(() => {
        setSeverity('success');
        setMessage('Password updated successfully');
        setOpen(true);

        setTimeout(() => setStep('LOGIN'), 1000);
      })
      .finally(() => setResetLoading(false));
  };

  if (pageLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{ color: 'white' }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 }
      }}
    >
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>

      <Grid2
        container
        justifyContent="right"
        alignItems="center"
        sx={{ width: '85%' }}
      >
        <Grid2 xs={12} sm={8} md={6} lg={4}>
          <Paper
            elevation={12}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            {/* Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 3, textAlign: 'center', color: 'white', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.1)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.1)' }} />

              <Box sx={{ width: 300, height: 90, mx: 'auto', mb: 2, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)', position: 'relative', zIndex: 1 }}>
                <img src={ngpicon} alt="Logo" style={{ width: '250px' }} />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, position: 'relative', zIndex: 1 }}>
                {step === 'LOGIN' ? 'Welcome Back' : step === 'FORGOT_EMAIL' ? 'Forgot Password' : step === 'OTP' ? 'Verify Identity' : 'Reset Password'}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.9, position: 'relative', zIndex: 1 }}>
                {step === 'LOGIN'
                  ? 'Sign in to your account'
                  : step === 'FORGOT_EMAIL'
                    ? 'Enter your registered email'
                    : step === 'OTP'
                      ? 'Enter 4-digit verification code'
                      : 'Set your new password'}
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Form Section */}
              <Box sx={{ width: '100%' }}>

                {/* LOGIN */}
                {step === 'LOGIN' && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        fullWidth
                        label="Username"
                        variant="outlined"
                        value={values.username}
                        disabled={loginLoading}
                        onChange={(e) => setValues({ ...values, username: e.target.value })}
                        onKeyPress={handleKeyPress}
                        InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon color="primary" /></InputAdornment> }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                      />
                    </Box>

                    <Box sx={{ mb: 4 }}>
                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        value={values.password}
                        disabled={loginLoading}
                        onChange={(e) => setValues({ ...values, password: e.target.value })}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LockIcon color="primary" /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                      />
                    </Box>

                    <Button fullWidth variant="contained" size="large" onClick={loginApi} disabled={loginLoading}
                      sx={{
                        py: 1.5, borderRadius: 2, fontSize: '1rem', fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)', boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)', transform: 'translateY(-2px)' },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {loginLoading ? <><CircularProgress size={20} sx={{ mr: 1 }} />Signing in...</> : 'Login'}
                    </Button>
                  </>
                )}

                {/* FORGOT EMAIL */}
                {step === 'FORGOT_EMAIL' && (
                  <>
                    <TextField
                      fullWidth
                      label="Registered Email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      onKeyPress={handleKeyPress}
                      sx={{ mb: 3 }}
                    />
                    <Button fullWidth variant="contained" size="large" onClick={submitForgotEmail} disabled={loginLoading}>
                      {loginLoading ? <><CircularProgress size={20} sx={{ mr: 1 }} />Sending...</> : 'Send OTP'}
                    </Button>
                  </>
                )}

                {/* OTP */}
                {step === 'OTP' && (
                  <>
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                      <Box sx={{
                        width: 70, height: 70, mx: 'auto', mb: 2, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #667eea'
                      }}>
                        <SecurityIcon sx={{ fontSize: 35, color: '#667eea' }} />
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        Enter the 4-digit OTP sent to your email
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      label="Enter OTP"
                      variant="outlined"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      onKeyPress={handleKeyPress}
                      disabled={otpVerifying}
                      InputProps={{ inputProps: { maxLength: 4, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '10px', fontWeight: 'bold' } } }}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)' } }}
                    />

                    <Button fullWidth variant="contained" size="large" onClick={verifyOtp} disabled={otpVerifying || otp.length !== 4}
                      sx={{
                        py: 1.5, borderRadius: 2, fontSize: '1rem', fontWeight: 600,
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                        '&:hover': { background: 'linear-gradient(135deg, #0da271 0%, #047857 100%)', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)', transform: 'translateY(-2px)' },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {otpVerifying ? <><CircularProgress size={20} sx={{ mr: 1 }} />Verifying...</> : 'Verify OTP'}
                    </Button>
                  </>
                )}

                {/* RESET PASSWORD */}
                {step === 'RESET_PASSWORD' && (
                  <>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      sx={{ mb: 3 }}
                    />
                    <Button fullWidth variant="contained" size="large" onClick={submitResetPassword} disabled={resetLoading}
                      sx={{
                        py: 1.5, borderRadius: 2, fontSize: '1rem', fontWeight: 600,
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                        '&:hover': { background: 'linear-gradient(135deg, #0da271 0%, #047857 100%)', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)', transform: 'translateY(-2px)' },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {resetLoading ? <><CircularProgress size={20} sx={{ mr: 1 }} />Updating...</> : 'Update Password'}
                    </Button>
                  </>
                )}

                {/* Forgot / Back button */}
                <Button onClick={step === 'LOGIN' ? handleForgotPassword : handleBackToLogin}>
                  {step === 'LOGIN' ? 'Forgot Password' : 'Back To Login'}
                </Button>

              </Box>

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {step === 'LOGIN' ? 'Secure authentication required' : 'Time-sensitive code'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  © 2026 NG PILLAI
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default LoginPage;
